import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    CrossSectionInput,
    CrossSectionPlane,
    crossSectionPlanesShareSlice,
} from '~/engine/util/algorithms/sdf/cross-section';

/** Identity of a list entry: stable across edits, never derived from position. */
export type CrossSectionId = number & { readonly __brand: 'CrossSectionId' };

/** One drawn cross-section; its plane is origin and normal in patient mm. */
export interface CrossSectionEntry {
    readonly id: CrossSectionId;
    readonly plane: CrossSectionPlane;
    readonly polygonsMm: readonly (readonly ReadonlyVec3[])[];
}

interface ListSnapshot {
    items: readonly CrossSectionEntry[];
    nextId: number;
}

const MAX_HISTORY_DEPTH = 100;
let lastRevision = 0;

const clonePolygons = (
    polygons: readonly (readonly ReadonlyVec3[])[]
): vec3[][] => polygons.map((polygon) => polygon.map((v) => vec3.clone(v)));

/** The drawn cross-sections in drawing order, with an undoable history. */
export class CrossSectionList {
    /** Replaced, never edited in place, so a history snapshot stays valid. */
    private items: readonly CrossSectionEntry[] = [];
    private readonly undoStack: ListSnapshot[] = [];
    private readonly redoStack: ListSnapshot[] = [];
    private nextId = 1;
    private _revision = ++lastRevision;

    /** Changes on every mutation and is never shared by two lists or two states of one list. */
    public get revision(): number {
        return this._revision;
    }

    public get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    public get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    public get size(): number {
        return this.items.length;
    }

    private snapshot(): ListSnapshot {
        return { items: this.items, nextId: this.nextId };
    }

    /** Record the current state for undo, drop the redo branch and apply `items`. */
    private commit(items: readonly CrossSectionEntry[]): void {
        this.undoStack.push(this.snapshot());
        if (this.undoStack.length > MAX_HISTORY_DEPTH) {
            this.undoStack.shift();
        }
        this.redoStack.length = 0;
        this.items = items;
        this._revision = ++lastRevision;
    }

    private step(from: ListSnapshot[], to: ListSnapshot[]): boolean {
        const target = from.pop();
        if (!target) {
            return false;
        }
        to.push(this.snapshot());
        this.items = target.items;
        // Restored with the items, so an id is never handed out twice.
        this.nextId = target.nextId;
        this._revision = ++lastRevision;
        return true;
    }

    public undo(): boolean {
        return this.step(this.undoStack, this.redoStack);
    }

    public redo(): boolean {
        return this.step(this.redoStack, this.undoStack);
    }

    /** Take the last drawing action back because it was refused, leaving nothing to redo. */
    public rejectLast(): boolean {
        return this.step(this.undoStack, []);
    }

    public add(plane: CrossSectionPlane, polygonsMm: vec3[][]): CrossSectionId {
        const id = this.nextId as CrossSectionId;
        const item: CrossSectionEntry = {
            id,
            plane: {
                originMm: vec3.clone(plane.originMm as vec3),
                normal: vec3.clone(plane.normal as vec3),
            },
            polygonsMm: clonePolygons(polygonsMm),
        };
        this.commit([...this.items, item]);
        this.nextId = id + 1;
        return id;
    }

    /** Add contours to an existing entry; they never replace what it holds. */
    public appendTo(id: CrossSectionId, polygonsMm: vec3[][]): boolean {
        if (!this.items.some((item) => item.id === id) || !polygonsMm.length) {
            return false;
        }
        const added = clonePolygons(polygonsMm);
        this.commit(
            this.items.map((item) =>
                item.id === id
                    ? { ...item, polygonsMm: [...item.polygonsMm, ...added] }
                    : item
            )
        );
        return true;
    }

    /** The entry on the same slice as `plane`, or null. Not a copy. */
    public findByPlane(plane: CrossSectionPlane): CrossSectionEntry | null {
        return (
            this.items.find((item) =>
                crossSectionPlanesShareSlice(item.plane, plane)
            ) ?? null
        );
    }

    /** Deep copies, so a caller cannot edit a drawn contour through them. */
    public toInputs(): CrossSectionInput[] {
        return this.items.map((item) => ({
            plane: item.plane,
            polygonsMm: clonePolygons(item.polygonsMm),
        }));
    }

    /** Undoable, since clearing everything is the easiest action to take by accident. */
    public clear(): boolean {
        if (this.items.length === 0) {
            return false;
        }
        this.commit([]);
        return true;
    }

    public clearHistory(): void {
        this.undoStack.length = 0;
        this.redoStack.length = 0;
    }
}
